import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { GraduationCap, Users, Sparkles, Mail, CheckCircle2, ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { CharacterAvatar } from "@/components/characters/CharacterAvatar";
import { ForgotPasswordDialog } from "@/components/auth/ForgotPasswordDialog";
import { KidsAiLogo } from "@/components/brand/KidsAiLogo";

const Login = () => {
  const [accountType, setAccountType] = useState<"school" | "individual">("individual");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [registrationMessage, setRegistrationMessage] = useState<{
    type: "success" | "info" | "warning";
    title: string;
    description: string;
  } | null>(null);
  
  const { signIn, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Randomly select a character for each page load
  const randomCharacter = useMemo(() => {
    const characters = ["robo", "sparkle", "buddy", "nova", "rocket", "star", "wise", "robo-purple"];
    return characters[Math.floor(Math.random() * characters.length)];
  }, []);

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Check for registration status from sessionStorage
  useEffect(() => {
    const pendingEmail = sessionStorage.getItem("pendingEmail");
    const status = sessionStorage.getItem("registrationStatus");

    if (pendingEmail) {
      setEmail(pendingEmail);
    }

    if (status === "email-verification-pending") {
      setRegistrationMessage({
        type: "success",
        title: "✅ Account Created Successfully!",
        description: `We've sent a verification email to ${pendingEmail}. Please check your inbox (and spam folder) and click the verification link. Once verified, you can log in here.`,
      });
      sessionStorage.removeItem("pendingEmail");
      sessionStorage.removeItem("registrationStatus");
    } else if (status === "duplicate-email") {
      setRegistrationMessage({
        type: "info",
        title: "👋 Welcome back!",
        description: `An account with ${pendingEmail} already exists. Please log in below or reset your password if needed.`,
      });
      sessionStorage.removeItem("pendingEmail");
      sessionStorage.removeItem("registrationStatus");
    } else if (status === "kids-setup-pending") {
      setRegistrationMessage({
        type: "warning",
        title: "✅ Your account is ready!",
        description: "Please log in to complete your children's profile setup.",
      });
      sessionStorage.removeItem("registrationStatus");
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      
      if (error) throw error;

      toast({
        title: "Welcome back!",
        description: "You have successfully signed in.",
      });

      navigate('/');
    } catch (error: any) {
      toast({
        title: "Login failed",
        description: error.message || "Invalid email or password",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-kid-blue/30 via-kid-purple/30 to-kid-pink/30 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Back to Home Button */}
      <Link 
        to="/" 
        className="absolute top-4 left-4 z-20 flex items-center gap-2 text-gray-700 hover:text-kid-purple font-fredoka font-bold bg-white/90 backdrop-blur px-4 py-2 rounded-xl shadow-lg hover:shadow-xl transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Home
      </Link>

      {/* Animated brand backdrop */}
      <div className="kid-hero-sheen" aria-hidden />
      <div className="kid-bg" aria-hidden>
        <div className="kid-bg-orb kid-bg-orb--purple" style={{ width: 360, height: 360, top: "-8%", left: "-6%" }} />
        <div className="kid-bg-orb kid-bg-orb--pink" style={{ width: 320, height: 320, bottom: "-10%", right: "-8%", animationDelay: "-15s" }} />
      </div>

      {/* Character mascot welcoming */}
      <div className="absolute top-8 right-8 hidden lg:block">
        <div className="relative">
          <CharacterAvatar character={randomCharacter as any} size="xl" animated />
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-2xl px-6 py-3 shadow-2xl">
            <p className="font-comic text-lg text-gray-700 whitespace-nowrap">
              Welcome back! 👋
            </p>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white transform rotate-45" />
          </div>
        </div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <KidsAiLogo className="mx-auto h-28 md:h-32 w-auto mb-3" />
          <p className="text-xl font-comic text-gray-700">Let's start learning! 🚀</p>
        </div>

        {/* Registration Success/Info Message */}
        {registrationMessage && (
          <Alert 
            className={`mb-6 border-2 animate-in fade-in slide-in-from-top-4 duration-500 ${
              registrationMessage.type === "success" ? "border-kid-green bg-kid-green/10" : 
              registrationMessage.type === "info" ? "border-kid-blue bg-kid-blue/10" : 
              "border-kid-yellow bg-kid-yellow/10"
            }`}
          >
            {registrationMessage.type === "success" && <Mail className="h-5 w-5 text-kid-green" />}
            {registrationMessage.type === "info" && <CheckCircle2 className="h-5 w-5 text-kid-blue" />}
            {registrationMessage.type === "warning" && <Sparkles className="h-5 w-5 text-kid-yellow" />}
            <AlertTitle className="font-fredoka font-bold text-lg">{registrationMessage.title}</AlertTitle>
            <AlertDescription className="font-comic text-base mt-2">
              {registrationMessage.description}
            </AlertDescription>
          </Alert>
        )}

        {/* Login Card */}
        <Card className="p-8 border-4 border-white shadow-2xl animate-in fade-in slide-in-from-bottom-6 duration-700">
          <Tabs value={accountType} onValueChange={(v) => setAccountType(v as any)} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 p-1 bg-gradient-to-r from-kid-blue/20 to-kid-purple/20 rounded-2xl">
              <TabsTrigger 
                value="individual" 
                className="gap-2 rounded-xl font-fredoka font-bold data-[state=active]:bg-white data-[state=active]:shadow-lg"
              >
                <Users className="w-4 h-4" />
                Kids & Families
              </TabsTrigger>
              <TabsTrigger 
                value="school" 
                className="gap-2 rounded-xl font-fredoka font-bold data-[state=active]:bg-white data-[state=active]:shadow-lg"
              >
                <GraduationCap className="w-4 h-4" />
                Schools
              </TabsTrigger>
            </TabsList>

            <TabsContent value="individual" className="space-y-6">
              <div className="text-center">
                <h2 className="text-3xl font-bubblegum font-bold text-gray-800 mb-2">
                  Welcome Back! 🎉
                </h2>
                <p className="text-lg font-comic text-gray-600">
                  Continue your adventure
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-fredoka font-semibold text-gray-700">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="you@example.com"
                    className="h-14 text-lg rounded-2xl border-2 focus:border-kid-purple"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="font-fredoka font-semibold text-gray-700">Password</Label>
                    <Button
                      type="button"
                      variant="link"
                      className="text-kid-purple hover:text-kid-pink font-fredoka font-bold text-sm h-auto p-0"
                      onClick={() => setForgotPasswordOpen(true)}
                    >
                      Forgot password? 🔐
                    </Button>
                  </div>
                  <Input 
                    id="password" 
                    type="password" 
                    placeholder="••••••••"
                    className="h-14 text-lg rounded-2xl border-2 focus:border-kid-purple"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-14 text-xl rounded-2xl bg-gradient-to-r from-kid-purple to-kid-pink font-fredoka font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2" />
                      Let's Go! 🚀
                    </>
                  )}
                </Button>
              </form>
              
              <div className="text-center">
                <p className="text-gray-600 font-comic">
                  New here?{" "}
                  <Link to="/register/parent" className="text-kid-purple hover:text-kid-pink font-fredoka font-bold">
                    Start your journey! ✨
                  </Link>
                </p>
              </div>
            </TabsContent>

            <TabsContent value="school" className="space-y-6">
              <div className="text-center">
                <h2 className="text-3xl font-bubblegum font-bold text-gray-800 mb-2">
                  School Login 🏫
                </h2>
                <p className="text-lg font-comic text-gray-600">
                  Access your school dashboard
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="school-email" className="font-fredoka font-semibold text-gray-700">Email</Label>
                  <Input 
                    id="school-email" 
                    type="email" 
                    placeholder="admin@school.edu"
                    className="h-14 text-lg rounded-2xl border-2 focus:border-kid-purple"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="school-password" className="font-fredoka font-semibold text-gray-700">Password</Label>
                    <Button
                      type="button"
                      variant="link"
                      className="text-kid-purple hover:text-kid-pink font-fredoka font-bold text-sm h-auto p-0"
                      onClick={() => setForgotPasswordOpen(true)}
                    >
                      Forgot password? 🔐
                    </Button>
                  </div>
                  <Input 
                    id="school-password" 
                    type="password" 
                    placeholder="••••••••"
                    className="h-14 text-lg rounded-2xl border-2 focus:border-kid-purple"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-14 text-xl rounded-2xl bg-gradient-to-r from-kid-purple to-kid-pink font-fredoka font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2" />
                      Sign In 🎓
                    </>
                  )}
                </Button>
              </form>
              
              <div className="text-center">
                <p className="text-gray-600 font-comic">
                  New school?{" "}
                  <Link to="/register/school" className="text-kid-purple hover:text-kid-pink font-fredoka font-bold">
                    Register now! 🏫
                  </Link>
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      <ForgotPasswordDialog 
        open={forgotPasswordOpen} 
        onOpenChange={setForgotPasswordOpen}
      />
    </div>
  );
};

export default Login;
